import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiGet, apiPost, ApiError } from "../lib/api";

type Plan = {
  id: number | string;
  label: string;
  audience: "personal" | "business" | string;
  monthly_price?: string;
  currencies: string[];
};

type RequestOptions = {
  plans: Plan[];
};

// Robust unwrapping so it works with { plans: [...] } or { data: { plans: [...] } }
function extractPlans(payload: any): Plan[] {
  const maybe = (payload?.plans ?? payload?.data?.plans) as unknown;
  return Array.isArray(maybe) ? (maybe as Plan[]) : [];
}

export default function AccountsRequest() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [planId, setPlanId] = useState<string>("");
  const [currency, setCurrency] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const payload = await apiGet<RequestOptions>("/xapi/accounts/request-options");
        if (!mounted) return;

        const validPlans = extractPlans(payload);
        setPlans(validPlans);

        if (validPlans.length) {
          const firstWithCurrency =
            validPlans.find((p) => (p.currencies?.length ?? 0) > 0) || validPlans[0];

          setPlanId(String(firstWithCurrency.id));
          if (firstWithCurrency.currencies?.length) {
            setCurrency(firstWithCurrency.currencies[0]);
          } else {
            setCurrency("");
          }
        } else {
          setPlanId("");
          setCurrency("");
        }
        setError(null);
      } catch (e: any) {
        setError(e?.message || "Failed to load request options.");
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const currencyOptions = useMemo(() => {
    const p = plans.find((pl) => String(pl.id) === planId);
    return p?.currencies ?? [];
  }, [plans, planId]);

  useEffect(() => {
    if (currencyOptions.length) {
      setCurrency((prev) => (currencyOptions.includes(prev) ? prev : currencyOptions[0]));
    } else {
      setCurrency("");
    }
  }, [planId, currencyOptions]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await apiPost("/xapi/accounts/request", { plan_id: planId, currency });
      navigate("/accounts");
      return;
    } catch (e: any) {
      if (e instanceof ApiError && e.validation) {
        setError(Object.values(e.validation)[0]?.[0] || e.message);
      } else {
        setError(e?.message || "Failed to create account.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen p-6">
        <div className="max-w-xl mx-auto">
          <h1 className="title-page mb-4">Request new funding account</h1>
          <div className="card-box">
            <div className="skeleton h-4 w-1/2 mb-2 rounded" />
            <div className="skeleton h-10 w-full rounded" />
          </div>
        </div>
      </div>
    );
  }

  const noOptions = !plans.length;

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-xl mx-auto">
        <h1 className="title-page mb-4">Request new funding account</h1>

        {noOptions ? (
  <div className="section-box p-4">
    <p className="text-sm text-gray-600">
      {plans.length === 0
        ? "Your funding account request is pending approval. You’ll be notified once it’s approved."
        : "No available funding plans/currencies right now."}
    </p>
  </div>
) : (
          <form onSubmit={onSubmit} className="form-section">
            {error && <div className="alert-danger">{error}</div>}

            <div>
              <label className="block text-sm font-medium mb-1">Funding Plan</label>
              <select
                className="w-full px-3 py-2 border rounded-md"
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                required
              >
                {plans.map((p) => (
                  <option key={p.id} value={String(p.id)}>
                    {p.label}
                    {p.monthly_price ? ` — ${p.monthly_price} EUR/month` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Currency</label>
              <select
                className="w-full px-3 py-2 border rounded-md"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                required
              >
                {currencyOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-3">
              <button
                type="submit"
                disabled={submitting || !planId || !currency}
                className="btn"
              >
                {submitting ? "Submitting…" : "Request account"}
              </button>
              <Link to="/accounts" className="nav-link">
                Cancel
              </Link>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
